import { useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, type CombinedCall, type CombinedSettings } from '../lib/api'
import { AudiencePicker, reachesNobody, useAudiences } from './AudiencePicker'

/** ONE REBROADCAST FOR SEVERAL OPEN CALLS (073, items 12-14).
 *
 *  The customer: "adjust the message for each of those three open calls, but
 *  it should go out in one text or one email", and "I should only be able to
 *  rebroadcast for three. The third one I will do when I want." Every open
 *  call is listed; each is ticked in or left out and carries one line -- a
 *  quick button (Settings, Messages) or words typed instead. Each starts with
 *  the button its going-count chooses (his Q1 A). Everyone on duty, or the
 *  groups ticked (his Q2 A).
 *
 *  THE SERVER COMPOSES THE MESSAGE, the preview and the send alike
 *  (`combined_rebroadcast.compose`): what is shown on the right is what
 *  goes. Every class carries `cb-`: theme.css owns the short names. */

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

type Pick = { on: boolean; line: number | null; own: string }

/** As `combined_rebroadcast.MAX_CALLS`: said here before the press. */
const MAX_CALLS = 20
/** Where the text server cuts a message. */
const TEXT_LIMIT = 1600

export function CombinedRebroadcast({ onClose, onSent }: {
  onClose: () => void
  /** After it went: how many calls, to how many members. */
  onSent: (calls: number, members: number) => void
}) {
  const [settings, setSettings] = useState<CombinedSettings | null>(null)
  const [calls, setCalls] = useState<CombinedCall[]>([])
  const [notBroadcast, setNotBroadcast] = useState(0)
  const [picks, setPicks] = useState<Record<string, Pick>>({})
  const [audience, setAudience] = useState<string[]>([])
  const audiences = useAudiences()
  const [preview, setPreview] = useState<{ whole: string; characters: number } | null>(null)
  const [previewNote, setPreviewNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  /** ONE PRESS, ONE MESSAGE: kept while nothing changes, new when it does. */
  const key = useRef(newKey())

  useEffect(() => {
    api.combinedWindow()
      .then((w) => {
        setSettings(w.settings)
        setCalls(w.calls)
        setNotBroadcast(w.not_broadcast ?? 0)
        setPicks(Object.fromEntries(w.calls.map((c) => [c.id, {
          on: c.start !== null,
          // A call that starts left out still has a button ready for the tick.
          line: c.start ?? w.settings.starts.one ?? 0,
          own: '',
        }])))
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not open the list of calls.'))
  }, [])

  /** The ticked calls, in the queue's order, with their line. */
  const items = useMemo(() => calls
    .filter((c) => picks[c.id]?.on)
    .map((c) => {
      const p = picks[c.id]
      const line = p.own.trim() || (p.line !== null ? settings?.lines[p.line]?.text ?? '' : '')
      return { incident_id: c.id, line }
    }), [calls, picks, settings])
  const missingLine = items.some((i) => !i.line)
  const itemsKey = JSON.stringify(items)
  useEffect(() => { key.current = newKey() }, [itemsKey, audience.join(',')])

  // WHAT THEY WILL RECEIVE, from the server's own composer, as the choices
  // change. A later answer only; nothing shown for nothing ticked.
  useEffect(() => {
    if (!items.length || missingLine) { setPreview(null); return }
    let alive = true
    const t = setTimeout(() => {
      api.combinedPreview(items)
        .then((p) => { if (alive) { setPreview(p); setPreviewNote(null) } })
        .catch((e) => { if (alive) { setPreview(null); setPreviewNote(e instanceof ApiError ? e.message : 'Could not show the message.') } })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey])

  // ESCAPE CLOSES IT, as every dialog here; never while it is sending.
  const dialog = useRef<HTMLDivElement>(null)
  useEffect(() => { dialog.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sending, onClose])

  const set = (id: string, patch: Partial<Pick>) =>
    setPicks((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  async function send() {
    if (sending || !items.length) return
    setSending(true)
    setError(null)
    try {
      const out = await api.combinedSend({ items, group_ids: audience, request_key: key.current })
      onSent(out.calls ?? items.length, out.recipients ?? 0)
    } catch (e) {
      // A REFUSAL IS IN WORDS; anything else may have gone before the answer
      // was lost -- and pressing again with the same choices is the same
      // message, never a second one.
      setError(e instanceof ApiError ? e.message
        : 'No answer from the server, so it is not known whether it went. Press Send again: it will not go twice.')
    } finally {
      setSending(false)
    }
  }

  const nobody = audience.length > 0 && reachesNobody(audiences, audience)
  const why = !items.length ? 'Tick at least one call.'
    : items.length > MAX_CALLS ? `${MAX_CALLS} calls at most in one rebroadcast — untick some.`
    : missingLine ? 'Every ticked call needs its line — press a button or type one.'
    : nobody ? 'Nobody on duty in the groups you chose.'
    : null

  return (
    <div className="cb-bg" role="dialog" aria-modal="true" aria-labelledby="cb-h">
      <div className="cb-modal" ref={dialog} tabIndex={-1}>
        <div className="cb-head">
          <h2 id="cb-h">Rebroadcast open calls — one message</h2>
          <button type="button" className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {!settings ? (
          <div className="cb-body1">{error ?? 'Loading…'}</div>
        ) : calls.length === 0 ? (
          <div className="cb-body1">No calls are open.</div>
        ) : (
          <div className="cb-body">
            <div className="cb-calls">
              <div className="cb-lab">1. Tick the calls to include, and say what each one needs</div>
              {notBroadcast > 0 && (
                <div className="cb-note">
                  {notBroadcast === 1 ? '1 open call has' : `${notBroadcast} open calls have`} not had
                  a first broadcast yet, so {notBroadcast === 1 ? 'it is' : 'they are'} not here: send
                  it from the call itself.
                </div>
              )}
              {calls.map((c) => {
                const p = picks[c.id]
                if (!p) return null
                return (
                  <div key={c.id} className={`cb-call ${c.going === 0 ? 'cb-none' : c.going === 1 ? 'cb-one' : 'cb-two'} ${p.on ? '' : 'cb-off'}`}>
                    <label className="cb-ct">
                      <input type="checkbox" checked={p.on} onChange={(e) => set(c.id, { on: e.target.checked })} />
                      <b>{c.nature_label}</b>
                      <small>{c.number} · {c.going ? `${c.going} going` : 'nobody going'}</small>
                    </label>
                    <div className="cb-where">{c.where}</div>
                    {p.on && (
                      <>
                        <div className="cb-chips">
                          {settings.lines.map((l, i) => (
                            <button type="button" key={i}
                                    className={`cb-chip ${l.urgent ? 'cb-urg' : ''} ${!p.own.trim() && p.line === i ? 'cb-on' : ''}`}
                                    aria-pressed={!p.own.trim() && p.line === i}
                                    onClick={() => set(c.id, { line: i, own: '' })}>
                              {l.text}
                            </button>
                          ))}
                        </div>
                        <input className="cb-own" maxLength={120} value={p.own}
                               aria-label={`Your own line for ${c.nature_label}`}
                               placeholder="…or type your own line"
                               onChange={(e) => set(c.id, { own: e.target.value })} />
                      </>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="cb-side">
              <div className="cb-lab">2. Send to</div>
              <AudiencePicker audiences={audiences} value={audience} onChange={setAudience} disabled={sending} />
              <div className="cb-lab" style={{ marginTop: 12 }}>3. What they will receive</div>
              <pre className="cb-preview">{preview?.whole ?? previewNote ?? (why ?? '…')}</pre>
              {preview && (
                <div className={`cb-count ${preview.characters > TEXT_LIMIT ? 'cb-over' : ''}`}>
                  {preview.characters} characters
                  {preview.characters > TEXT_LIMIT
                    ? ` — too long for one text (${TEXT_LIMIT}); it will be refused if anyone gets texts. Leave some calls out or shorten the lines.`
                    : ''}
                </div>
              )}
            </div>
          </div>
        )}
        {error && settings && <div className="notice cb-err">{error}</div>}
        <div className="cb-foot">
          <span className="cb-hint">{why ?? `${calls.length - items.length} call${calls.length - items.length === 1 ? '' : 's'} left out`}</span>
          <button type="button" className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button type="button" className="btn primary" disabled={!!why || sending || !settings} onClick={send}>
            {sending ? 'Sending…' : `Send ${items.length} call${items.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
